import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

export interface Pillar {
  title: string
  description: string
  bullets?: string[]
}

interface SolutionPillarsProps {
  eyebrow?: string
  title: ReactNode
  body?: string
  pillars: Pillar[]
}

export default function SolutionPillars({
  eyebrow = 'What you get',
  title,
  body,
  pillars,
}: SolutionPillarsProps) {
  return (
    <Section tone="subtle" density="spacious">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            {title}
          </Heading>
          {body && (
            <Text size="xl" tone="secondary" className="mt-6">
              {body}
            </Text>
          )}
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE, delay: (i % 2) * 0.06 }}
              className="rounded-3xl border border-line-subtle bg-surface p-8 lg:p-10 shadow-card"
            >
              <h3 className="text-display-sm font-medium text-ink-primary">
                {p.title}
              </h3>
              <Text size="md" tone="secondary" className="mt-3">
                {p.description}
              </Text>
              {p.bullets && p.bullets.length > 0 && (
                <ul className="mt-6 space-y-2.5">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex gap-3 text-body-sm text-ink-secondary"
                    >
                      <CheckIcon className="mt-1 h-4 w-4 flex-shrink-0 text-accent-600" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.15" />
      <path
        d="M4.5 8.2 7 10.5l4.5-4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
